import React from 'react';
import assertions from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Dialog from 'components/Dialog';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Label from 'components/Label';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import CoachingConstants from 'coaching/CoachingConstants';
import {
  addStudentByDevice,
  AddStudentFn,
  addStudentToTutoringByDevice,
} from 'coaching/actions/CoachingActions';

type Props = {
  onRequestHide: () => void;
  onAddChild?: (studentEmail: string) => void;
  participantUUID?: string;
  currentUserEmail?: string;
  linkStudentToken?: string;

  // because this modal can appear in multiple contexts of the coaching UI,
  // we want to be able to change the `screen` component of the UI event key
  eventKeyScreen?: string;
};

/** Add child modal for the old signup flow. We can remove this when that flow
 * is deleted
 */
export default function CoachingSelfServeAddChildAccountModal(props: Props) {
  return <CoachingSelfServeAddChildAccountModalImpl addStudentFn={addStudentByDevice} {...props} />;
}

/** Add child modal for the new signup flow. */
export function TutoringSelfServeAddChildAccountModal(props: Props) {
  return (
    <CoachingSelfServeAddChildAccountModalImpl
      addStudentFn={addStudentToTutoringByDevice}
      {...props}
    />
  );
}

function CoachingSelfServeAddChildAccountModalImpl(props: Props & { addStudentFn: AddStudentFn }) {
  const {
    onRequestHide,
    onAddChild,
    participantUUID,
    currentUserEmail,
    linkStudentToken,
    eventKeyScreen,
  } = props;
  const eventKeyPrefix = eventKeyScreen
    ? `coaching.${eventKeyScreen}.add_student_modal`
    : 'coaching.add_student_modal';
  const trackEvent = (eventName: string) =>
    EventTracking.trackKey(`${eventKeyPrefix}.${eventName}`);

  if (!participantUUID) {
    return (
      <Dialog title="Something went wrong">
        <ErrorReporter error="CoachingSelfServeAddChildAccountModal - tried to mount without a participant UUID">
          Oops! Looks like something went wrong - please contact us at{' '}
          {CoachingConstants.COACHING_HELP_EMAIL}
          to get your student added on Remind.
        </ErrorReporter>
      </Dialog>
    );
  }

  const [studentHasAccount, setStudentHasAccount] = React.useState<boolean | undefined>(undefined);
  const [studentDeviceUid, setStudentEmail] = React.useState<string | undefined>(undefined);

  const addChildHeader = <Text font="serif">Invite student</Text>;
  const resetDialogState = () => {
    setStudentEmail(undefined);
    setStudentHasAccount(undefined);
  };
  const handleClick = async () => {
    if (!studentDeviceUid) {
      console.error('CoachingSelfServeAddChildAccountModal - tried to submit without a device UID');
      return ToastActions.showErrorToast(
        `Oops! Looks like something went wrong - please contact us at ${CoachingConstants.COACHING_HELP_EMAIL} to get your student added on Remind.`
      );
    }
    const resp = await props.addStudentFn({
      userDevice: studentDeviceUid,
      participantUUID,
      userEmail: currentUserEmail,
      linkStudentToken,
    });
    if (resp.success && onAddChild) {
      onAddChild(studentDeviceUid);
    }
    onRequestHide();
  };
  const handleRequestHide = (eventContext?: string) => {
    trackEvent(`${eventContext ? eventContext + '.' : ''}close.click`);
    onRequestHide();
  };

  const parentEnteredTheirOwnEmail = studentDeviceUid === currentUserEmail;
  const parentEnteredTheirOwnEmailCopy = parentEnteredTheirOwnEmail
    ? 'If you and your student share a Remind account, you don’t need to go through this step and can close this dialog.'
    : undefined;

  const submitButton = (label: string, eventContext: string) => (
    <Button
      data-test="submit-button"
      level="primary"
      color="mascot"
      onClick={handleClick}
      disabled={!studentDeviceUid || parentEnteredTheirOwnEmail}
      eventKey={`${eventKeyPrefix}.${eventContext}.add_account.click`}>
      {label}
    </Button>
  );
  const backButton = (eventContext: string) => (
    <Button
      data-test="back-button"
      level="text"
      color="graphite"
      onClick={resetDialogState}
      eventKey={`${eventKeyPrefix}.${eventContext}.back.click`}>
      Back
    </Button>
  );

  const initialDialog = (
    <Dialog
      data-test="initial-dialog"
      textAlign="left"
      size="small"
      onRequestHide={() => handleRequestHide()}
      title={addChildHeader}
      subtext="Let them connect with the tutor and join sessions from their own account. You’ll still stay in the loop."
      eventKey={`${eventKeyPrefix}.view`}>
      <BoxBody>
        <Row size="small">
          <Label>Does your student have a Remind account?</Label>
        </Row>
        <Buttons axis="y" level="secondary" color="graphite">
          <Button
            data-test="yes-button"
            onClick={() => setStudentHasAccount(true)}
            eventKey={`${eventKeyPrefix}.yes.click`}>
            Yes
          </Button>
          <Button
            data-test="no-button"
            onClick={() => setStudentHasAccount(false)}
            eventKey={`${eventKeyPrefix}.no.click`}>
            No
          </Button>
          <Button
            data-test="unsure-button"
            onClick={() => setStudentHasAccount(false)}
            eventKey={`${eventKeyPrefix}.unsure.click`}>
            I’m not sure
          </Button>
        </Buttons>
      </BoxBody>
    </Dialog>
  );

  const studentHasAccountDialog = (
    <Dialog
      data-test="student-has-account-dialog"
      size="small"
      onRequestHide={() => handleRequestHide('existing_account')}
      title={addChildHeader}
      subtext="Enter the email associated with your student’s Remind account. We’ll send an invitation to get started with Remind Tutoring and connect you as family members."
      eventKey={`${eventKeyPrefix}.existing_account.view`}>
      <BoxBody flush>
        <Row size="xsmall">
          <FormElement
            validate={e => {
              assertions.email(e.target.value);
            }}
            onValidChange={e => setStudentEmail(e.target.value)}
            onInvalidChange={() => setStudentEmail(undefined)}
            label="Student's email"
            subtext={parentEnteredTheirOwnEmailCopy}
            element={Input}
            elementProps={{
              placeholder: 'example@email.com',
              onClick: () => trackEvent('existing_account.device_input.click'),
            }}
            trimWhitespace
          />
        </Row>
        <Row size="small">
          <Buttons axis="y">
            {submitButton('Send invitation', 'existing_account')}
            {backButton('existing_account')}
          </Buttons>
        </Row>
      </BoxBody>
    </Dialog>
  );

  const studentHasNoAccountDialog = (
    <Dialog
      data-test="student-has-no-account-dialog"
      size="small"
      onRequestHide={() => handleRequestHide('new_account')}
      title={addChildHeader}
      subtext="We’ll send an email invitation to get started with Remind Tutoring."
      eventKey={`${eventKeyPrefix}.new_account.view`}>
      <BoxBody flush>
        <Row size="xsmall">
          <FormElement
            data-test="student-email-input"
            validate={e => {
              assertions.email(e.target.value);
            }}
            onValidChange={e => setStudentEmail(e.target.value)}
            onInvalidChange={() => setStudentEmail(undefined)}
            label="Student’s email"
            subtext={parentEnteredTheirOwnEmailCopy}
            element={Input}
            elementProps={{
              placeholder: 'example@email.com',
              onClick: () => trackEvent('new_account.device_input.click'),
            }}
            trimWhitespace
          />
        </Row>
        <Row size="small">
          <Buttons axis="y">
            {submitButton('Send invitation', 'new_account')}
            {backButton('new_account')}
          </Buttons>
        </Row>
      </BoxBody>
    </Dialog>
  );

  if (studentHasAccount === undefined) {
    return initialDialog;
  }

  return studentHasAccount ? studentHasAccountDialog : studentHasNoAccountDialog;
}
